/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React, {
  useState, useContext, useMemo, useEffect, useRef
} from 'react'
import PropTypes from 'prop-types'
import Heading3 from '@apps/components/Typography/Heading3'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import {
  setSimNumber,
  SIM_SELECTION_STATE,
  updateSimSelectionState
} from '@apps/redux/features/SimSelectionSlice'
import SimSelectionChangeHandlers, {
  SIM_SELECTION_EVENTS
} from '@apps/utils/ChangeHandlers/SimSelectionChangeHandlers'
import classNames from 'classnames'
import { getBySlug } from '@apps/utils/contentful'
import {
  getSimSelectionPageContent,
  getSimSelectionSlugValue
} from '@apps/utils/simSelectionContent'
import Sites from '@sites/index'
import { useDispatch, useSelector } from 'react-redux'
import {
  formatSimField
} from '@apps/utils/formHelpers'
import {
  Icon,
  Input as KoodoInput,
  Typography
} from '@telus/koodo-ui-kit'

const MAX_LENGTH = 28
const CHUNK_SIZE = 5
const DASH_LENGTH = 3 // number of ' - '
const DASH_REGEX = /\s-\s/g
const SIM_INPUT_LABEL_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 16,
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: '24px'
}
const SIM_INPUT_HELPER_TOKENS = {
  color: '#000000',
  fontSize: 14,
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: '20px'
}
const SIM_INPUT_HELPER_LINK_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: '20px'
}
const SIM_FEEDBACK_TOKENS = {
  color: '#000000',
  fontSize: 14,
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: '20px'
}

const SIM = ({
  simRef, className = '', userType = '', showHeading = true
}) => {
  const dispatch = useDispatch()
  const simInputContainerRef = useRef(null)
  const { pageContent } = useSelector((state) => state.common.content)

  const appState = useContext(ApplicationContext)
  const simSelectionState = useSelector((state) => state.simSelectionPage)
  const { isSimValidInfo } = useSelector((state) => state.common)

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: currentLanguage,
    [APPLICATION_STATE.SERVICES]: brandServices
  } = appState
  const { isSimValid } = brandServices
  const { [SIM_SELECTION_STATE.SIM]: rawSimValue, [SIM_SELECTION_STATE.SIM_ERROR]: error } =
    simSelectionState
  const simValue = rawSimValue || ''
  const [simValidationPassed, setSimValidationPassed] = useState(
    () => Boolean(simValue) && simValue.length === MAX_LENGTH && error === null
  )

  const simSelectionPageContent = useMemo(() => {
    return getSimSelectionPageContent(pageContent, currentLanguage, userType)
  }, [pageContent, currentLanguage, userType])

  const updateSimSelectionStore = (newState) => {
    dispatch(updateSimSelectionState(newState))
  }

  const { shortTextFields } = simSelectionPageContent

  const textContent = useMemo(() => {
    if (!shortTextFields) return {}
    return {
      simHeading: getBySlug(shortTextFields, 'simHeading')?.value,
      simLabel: getBySlug(shortTextFields, 'simLabel')?.value,
      simCardNumberInputLabel:
        getSimSelectionSlugValue(shortTextFields, 'simCardNumberInputLabel', userType),
      physicalSimStoreLocatorMessage:
        getSimSelectionSlugValue(shortTextFields, 'physicalSimStoreLocatorMessage', userType),
      simUnavailableError:
        getSimSelectionSlugValue(shortTextFields, 'simUnavailableError', userType),
      simRequiredError: getSimSelectionSlugValue(shortTextFields, 'simRequiredError', userType),
      simLengthError: getSimSelectionSlugValue(shortTextFields, 'simLengthError', userType),
      simCardNumberValidated:
        getSimSelectionSlugValue(shortTextFields, 'simCardNumberValidated', userType),
      storeLocator: getSimSelectionSlugValue(shortTextFields, 'storeLocator', userType),
      storeLink: getSimSelectionSlugValue(shortTextFields, 'storeLink', userType)
    }
  }, [shortTextFields, userType])

  const { simRequiredError, simLengthError, simUnavailableError } = textContent

  useEffect(() => {
    if (simSelectionState.simError) {
      handleSimBlur(setLoading)
    }
  }, [currentLanguage])

  const [loading, setLoading] = useState(false)
  const errors = {
    simRequiredError,
    simLengthError,
    simUnavailableError
  }
  const onSimBlur = async () => {
    return SimSelectionChangeHandlers[SIM_SELECTION_EVENTS.SIM].onBlur(
      simSelectionState,
      isSimValid,
      setLoading,
      errors,
      updateSimSelectionStore
    )
  }
  useEffect(() => {
    ; (async () => {
      document.body.style.overflow = 'auto'
      if (!isSimValidInfo || simSelectionState.simError) {
        await onSimBlur()
      }
    })()
  }, [!isSimValidInfo])

  const updateStoreSimNumber = (newState) => {
    const simNumber = newState[SIM_SELECTION_STATE.SIM]
    dispatch(setSimNumber(simNumber))
  }

  const onSimNumberKeyDown = (event) => {
    const input = event.target
    const cursorPosition = input.selectionStart
    const cursorEndPosition = input.selectionEnd

    // If text is selected, do not handle special backspace behavior
    if (cursorPosition !== cursorEndPosition) {
      return
    }

    if (event.key === 'Backspace' && cursorPosition > 0) {
      const {value} = input

      const isDeletingNumber = value[cursorPosition - 2] === ' ' &&
        value[cursorPosition - 3] === '-' &&
        value[cursorPosition - 4] === ' '
      
        const isDeletingSpace = value[cursorPosition - 1] === ' ' &&
        value[cursorPosition - 2] === '-' &&
        value[cursorPosition - 3] === ' '

      if (isDeletingNumber || isDeletingSpace) {
        event.preventDefault()
        // Remove the space and dash before the cursor
        const newValue =
          value.slice(0, cursorPosition - 3) + value.slice(cursorPosition)

        if (formatSimField(newValue)?.formattedSim !== newValue) {
          SimSelectionChangeHandlers[SIM_SELECTION_EVENTS.SIM].onChange(
            newValue,
            updateStoreSimNumber
          )
          const positionOffset = isDeletingNumber ? 4 : 3
          const newCursorPosition = cursorPosition - positionOffset
          window.requestAnimationFrame(() => {
            input.setSelectionRange(newCursorPosition, newCursorPosition)
          })
        }
      }
    }
  }

  const getSimInputElement = () => {
    const { activeElement } = document
    if (
      activeElement?.tagName === 'INPUT' &&
      simInputContainerRef.current?.contains(activeElement)
    ) {
      return activeElement
    }

    return simInputContainerRef.current?.querySelector('input')
  }

  const onSimNumberChange = (value) => {
    const input = getSimInputElement()
    const cursorPosition = input?.selectionStart ?? value.length
    setSimValidationPassed(false)

    // Calculate cursor position in raw value (without dashes)
    const dashesBeforeCursor = (simValue.slice(0, cursorPosition).match(DASH_REGEX) || []).length
    const cursorPositionInRawValue = cursorPosition - (dashesBeforeCursor * DASH_LENGTH)

    SimSelectionChangeHandlers[SIM_SELECTION_EVENTS.SIM].onChange(
      value,
      updateStoreSimNumber
    )

    // Restore cursor position after re-render
    window.requestAnimationFrame(() => {
      if (!input) return
      // If cursor is at the end, move it to the new end
      if (cursorPosition === simValue.length) {
        const newLength = input.value.length
        input.setSelectionRange(newLength, newLength)
        return
      }

      // Otherwise calculate position based on chunks
      const completedChunks = Math.floor(cursorPositionInRawValue / CHUNK_SIZE)
      const newPosition = cursorPositionInRawValue + (completedChunks * DASH_LENGTH)
      input.setSelectionRange(newPosition, newPosition)
    })
  }

  const handleSimBlur = async () => {
    const simResult = await onSimBlur()
    const isCurrentSimComplete = simSelectionState[SIM_SELECTION_STATE.SIM]?.length === MAX_LENGTH
    const isCurrentSimValid = !simResult && isCurrentSimComplete

    setSimValidationPassed(isCurrentSimValid)

    if (simResult) {
      return true
    }
    return false
  }

  const getDisplayError = () => {
    if (!error) return ''
    return error
  }

  const simHasError = !loading && Boolean(error)
  const simIsComplete = simValue?.length === MAX_LENGTH
  const simIsValid = !loading && simValidationPassed && simIsComplete && error === null
  const displayError = getDisplayError()

  const getSIMInputClassName = () => {
    if (simHasError) return 'relative w-full kpre-sim-error-input'
    if (simIsValid) return 'relative w-full kpre-sim-success-input'
    return 'relative w-full'
  }

  const getSIMInputValidation = () => {
    if (simHasError) return 'error'
    if (simIsValid) return 'success'
    return undefined
  }

  const getSIMInputTokens = () => {
    const defaultTokens = {
      borderRadius: 4,
      height: 44
    }

    if (simHasError) {
      return {
        ...defaultTokens,
        backgroundColor: '#ffffff',
        borderColor: '#c9370b',
        borderWidth: 1,
        outerBorderColor: 'transparent',
        outerBorderWidth: 0,
        icon: null,
        iconColor: 'rgba(0, 0, 0, 0)',
        paddingRight: 56
      }
    }

    if (simIsValid) {
      return {
        ...defaultTokens,
        backgroundColor: '#ffffff',
        borderColor: '#1c7b2b',
        borderWidth: 1,
        outerBorderColor: 'transparent',
        outerBorderWidth: 0,
        icon: null,
        iconColor: 'rgba(0, 0, 0, 0)',
        paddingRight: 56
      }
    }

    return defaultTokens
  }

  return (
    <div ref={simRef} className={classNames('relative', className)}>
      {showHeading && textContent?.simHeading && userType === Sites.consumer && (
        <Heading3 className="pb-4">{textContent?.simHeading}</Heading3>
      )}
      <div className="mb-2">
        <div
          id="sim-number-label"
          data-contentful-slug="simCardNumberInputLabel"
        >
          <Typography
            variant={{ size: 16, bold: true, color: '#000000' }}
            tokens={SIM_INPUT_LABEL_TOKENS}
          >
            {textContent?.simCardNumberInputLabel}
          </Typography>
        </div>
        <div>
          <Typography
            variant={{ size: 14, color: '#000000' }}
            tokens={SIM_INPUT_HELPER_TOKENS}
          >
            <span data-contentful-slug="physicalSimStoreLocatorMessage">
              {textContent?.physicalSimStoreLocatorMessage}
            </span>
            {' '}
          </Typography>
          {textContent?.storeLink ? (
            <a
              className="text-black underline"
              href={textContent.storeLink}
              target="_blank"
              rel="noopener noreferrer"
              data-contentful-slug="storeLocator"
              data-contentful-link-slug="storeLink"
            >
              <Typography
                variant={{ size: 14, bold: true, color: '#000000' }}
                tokens={SIM_INPUT_HELPER_LINK_TOKENS}
              >
                {textContent?.storeLocator}
              </Typography>
            </a>
          ) : (
            <span className="underline" data-contentful-slug="storeLocator">
              <Typography
                variant={{ size: 14, bold: true, color: '#000000' }}
                tokens={SIM_INPUT_HELPER_LINK_TOKENS}
              >
                {textContent?.storeLocator}
              </Typography>
            </span>
          )}
        </div>
      </div>
      <div ref={simInputContainerRef} className={getSIMInputClassName()}>
        <KoodoInput
          type="text"
          inputMode="numeric"
          pattern="[0-9 -]*"
          id="sim"
          label=""
          aria-label={textContent?.simCardNumberInputLabel || textContent?.simLabel || 'SIM card number'}
          aria-labelledby="sim-number-label"
          name="sim-number"
          maxLength={MAX_LENGTH}
          value={simValue}
          onBlur={() => handleSimBlur()}
          onChange={onSimNumberChange}
          onKeyDown={onSimNumberKeyDown}
          validation={getSIMInputValidation()}
          tokens={getSIMInputTokens()}
          testId="accountSetup-sim"
        />
        {simIsValid && (
          <span
            className="pointer-events-none absolute bottom-[10px] right-4 flex h-6 w-6 items-center justify-center"
            data-testid="accountSetup-sim-valid"
            aria-hidden="true"
          >
            <Icon icon="CheckCircleFilled" variant={{ color: 'success', size: 'small' }} />
          </span>
        )}
        {simHasError && (
          <span
            className="pointer-events-none absolute bottom-[10px] right-4 flex h-6 w-6 items-center justify-center"
            data-testid="accountSetup-sim-invalid"
            aria-hidden="true"
          >
            <Icon icon="ExclamationOctagon" variant={{ color: 'danger', size: 'small' }} />
          </span>
        )}
      </div>
      {simHasError && (
        <div
          className="mt-1 w-full bg-[#fbd1c5] px-4 py-3 text-black"
          data-testid="accountSetup-sim-error"
        >
          <Typography
            variant={{ size: 14, color: '#000000' }}
            tokens={SIM_FEEDBACK_TOKENS}
          >
            {displayError}
          </Typography>
        </div>
      )}
      {simIsValid && (
        <div
          className="mt-1 w-full bg-[#c8f5d1] px-4 py-3 text-black"
          data-testid="accountSetup-sim-success"
          data-contentful-slug="simCardNumberValidated"
        >
          <Typography
            variant={{ size: 14, color: '#000000' }}
            tokens={SIM_FEEDBACK_TOKENS}
          >
            {textContent?.simCardNumberValidated}
          </Typography>
        </div>
      )}
    </div>
  )
}

SIM.propTypes = {
  simRef: PropTypes.object.isRequired,
  className: PropTypes.string,
  userType: PropTypes.string,
  showHeading: PropTypes.bool
}

export default SIM
